import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@repo/ui/src/components/ui/card';
import { Switch } from '@repo/ui/src/components/ui/switch';
import { cn } from '@repo/ui/src/lib/utils';
import {
  User,
  Users,
  UsersRound,
  MessageCircle,
  Mail,
  MessagesSquare,
  Banknote,
  DollarSign,
  ArrowLeftRight,
  Check,
  Building2,
  Home,
} from 'lucide-react';
import { useOrgSettings, useUpdatePreferences, useUpdateOrgSettings } from '../../../hooks/useOrgSettings';
import { useSubscription } from '../../../hooks/useSubscription';
import type { OnboardingInput, UpdateOrgSettingsInput } from '@repo/validation';
import type { ClassMode } from '@repo/shared-types';
import { SectionLoading } from '../components/SectionLoading';
import { BusyButton } from '../../../components/BusyButton';

/** The Lớp học page's ⋮ menu ("Cách tổ chức lớp…", THI-267 Task 10) deep
 *  links to `/settings/display#class-mode` — scrolled to on mount below,
 *  same pattern as `TEMPLATE_CARD_ANCHOR_ID` in BillingRulesSection. */
const CLASS_MODE_CARD_ANCHOR_ID = 'class-mode';

/** The display-only preference fields — everything on this section goes
 *  through `useUpdatePreferences` (POST /auth/onboarding), unlike the money
 *  fields in `BillingRulesSection`. */
type DisplayField = 'businessType' | 'sellsExtras' | 'teamSize' | 'showTeacherAssignment' | 'preferredChannel' | 'defaultCurrency';

interface OptionDef {
  value: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

interface PreferenceSection {
  key: Extract<DisplayField, 'businessType' | 'teamSize' | 'preferredChannel' | 'defaultCurrency'>;
  titleKey: string;
  options: OptionDef[];
}

// Order here drives the on-screen order (interleaved with the two Switch cards
// in the render below): businessType → [sellsExtras] → teamSize →
// [showTeacherAssignment] → preferredChannel → defaultCurrency.
const PREFERENCE_SECTIONS: PreferenceSection[] = [
  {
    key: 'businessType',
    titleKey: 'preferences.businessType',
    options: [
      { value: 'UNREGISTERED', labelKey: 'preferences.businessTypeUnregistered', icon: User },
      { value: 'HOUSEHOLD', labelKey: 'preferences.businessTypeHousehold', icon: Home },
      { value: 'ENTERPRISE', labelKey: 'preferences.businessTypeEnterprise', icon: Building2 },
    ],
  },
  {
    key: 'teamSize',
    titleKey: 'preferences.teamSize',
    options: [
      { value: 'SOLO', labelKey: 'preferences.SOLO', icon: User },
      { value: 'SMALL', labelKey: 'preferences.SMALL', icon: Users },
      { value: 'MEDIUM', labelKey: 'preferences.MEDIUM', icon: UsersRound },
    ],
  },
  {
    key: 'preferredChannel',
    titleKey: 'preferences.preferredChannel',
    options: [
      { value: 'ZALO', labelKey: 'preferences.ZALO', icon: MessageCircle },
      { value: 'EMAIL', labelKey: 'preferences.EMAIL', icon: Mail },
      { value: 'BOTH', labelKey: 'preferences.BOTH_channel', icon: MessagesSquare },
    ],
  },
  {
    key: 'defaultCurrency',
    titleKey: 'preferences.defaultCurrency',
    options: [
      { value: 'VND', labelKey: 'preferences.VND', icon: Banknote },
      { value: 'USD', labelKey: 'preferences.USD', icon: DollarSign },
      { value: 'BOTH', labelKey: 'preferences.BOTH_currency', icon: ArrowLeftRight },
    ],
  },
];

/** Inner form component — mounts with correct initial values from server */
function DisplayForm({
  initialValues,
  initialClassMode,
}: {
  initialValues: OnboardingInput;
  initialClassMode: ClassMode;
}) {
  const { t } = useTranslation('settings');
  const [values, setValues] = useState<OnboardingInput>(initialValues);
  // Per-field, not one boolean: `POST /auth/onboarding` takes every field as
  // optional, so a save sends only what this section actually touched and
  // never re-writes a field the teacher did not change.
  const [dirtyFields, setDirtyFields] = useState<ReadonlySet<DisplayField>>(new Set());
  // classMode (THI-267) is written through `PUT /org/settings` instead —
  // `useOrgSettings`'s response type documents that route as its owner (same
  // partial-update contract as `maxExcusedPerMonth` and friends), so it gets
  // its own state and its own mutation, mirrored on the money fields in
  // BillingRulesSection rather than folded into `values`/`dirtyFields` above.
  const [classMode, setClassMode] = useState<ClassMode>(initialClassMode);
  const [hasClassModeChanges, setHasClassModeChanges] = useState(false);
  // Silent: this section owns the single toast for the save action (spec §4's
  // double-toast fix). The hook's error toast still fires.
  const updatePreferences = useUpdatePreferences({ silent: true });
  const updateOrgSettings = useUpdateOrgSettings();
  const { data: sub } = useSubscription();
  const extrasAllowed = sub?.plan.allowsExtras ?? false;

  // Scroll the "Tổ chức lớp" row into view when arriving from the Lớp học
  // page's ⋮ menu deep link (`#class-mode`).
  useEffect(() => {
    if (window.location.hash === `#${CLASS_MODE_CARD_ANCHOR_ID}`) {
      document.getElementById(CLASS_MODE_CARD_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  function markDirty(field: DisplayField) {
    setDirtyFields((prev) => new Set(prev).add(field));
  }

  function handleSelectClassMode(next: ClassMode) {
    setClassMode(next);
    setHasClassModeChanges(true);
  }

  function handleSelect(key: PreferenceSection['key'], value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
    markDirty(key);
  }

  function handleToggleShowTeacherAssignment(next: boolean) {
    setValues((prev) => ({ ...prev, showTeacherAssignment: next }));
    markDirty('showTeacherAssignment');
  }

  function handleToggleSellsExtras(next: boolean) {
    setValues((prev) => ({ ...prev, sellsExtras: next }));
    markDirty('sellsExtras');
  }

  // Two mutations can fire from one click (preferences + classMode), each
  // gated on its own dirty flag, same pattern as BillingRulesSection's
  // handleSave: one success toast fires only once every mutation this click
  // asked for has resolved; a rejection has already toasted its own error.
  async function handleSave() {
    const requests: Promise<unknown>[] = [];

    if (dirtyFields.size > 0) {
      const payload: OnboardingInput = {};
      if (dirtyFields.has('businessType')) payload.businessType = values.businessType;
      if (dirtyFields.has('sellsExtras')) payload.sellsExtras = values.sellsExtras;
      if (dirtyFields.has('teamSize')) payload.teamSize = values.teamSize;
      if (dirtyFields.has('showTeacherAssignment')) payload.showTeacherAssignment = values.showTeacherAssignment;
      if (dirtyFields.has('preferredChannel')) payload.preferredChannel = values.preferredChannel;
      if (dirtyFields.has('defaultCurrency')) payload.defaultCurrency = values.defaultCurrency;

      requests.push(
        updatePreferences.mutateAsync(payload, { onSuccess: () => setDirtyFields(new Set()) }),
      );
    }

    if (hasClassModeChanges) {
      const payload: UpdateOrgSettingsInput = { classMode };
      requests.push(
        updateOrgSettings.mutateAsync(payload, { onSuccess: () => setHasClassModeChanges(false) }),
      );
    }

    // Defensive: the Save button is disabled while both dirty flags are
    // false, so this is unreachable from the UI — but an empty Promise.all
    // would resolve instantly and toast "saved" for a save that wrote nothing.
    if (requests.length === 0) return;

    try {
      await Promise.all(requests);
    } catch {
      return;
    }
    toast.success(t('saved'));
  }

  function renderSection(section: PreferenceSection) {
    return (
      <Card key={section.key}>
        <CardHeader>
          <CardTitle className="text-base">{t(section.titleKey)}</CardTitle>
          <CardDescription className="sr-only">
            {t(section.titleKey)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {section.options.map((option) => {
              const isSelected = values[section.key] === option.value;
              const Icon = option.icon;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(section.key, option.value)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                    isSelected
                      ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                      : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                  )}
                >
                  <Icon
                    className={cn(
                      'w-4 h-4',
                      isSelected ? 'text-brand-600' : 'text-surface-400',
                    )}
                    strokeWidth={1.8}
                  />
                  {t(option.labelKey)}
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-brand-600" strokeWidth={2.5} />
                  )}
                </button>
              );
            })}
          </div>
          {section.key === 'businessType' && (
            <p className="mt-3 text-xs text-surface-500">{t('preferences.businessTypeHelper')}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const [businessSection, teamSizeSection, ...tailSections] = PREFERENCE_SECTIONS;

  return (
    <div className="space-y-6">
      {/* 1. Hình thức kinh doanh */}
      {businessSection && renderSection(businessSection)}

      {/* 2. Bán thêm hàng/dụng cụ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.sellsExtras')}</CardTitle>
          <CardDescription>{t('preferences.sellsExtrasDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Switch
            checked={extrasAllowed ? (values.sellsExtras ?? false) : false}
            disabled={!extrasAllowed}
            onCheckedChange={(next) => extrasAllowed && handleToggleSellsExtras(next)}
            aria-label={t('preferences.sellsExtras')}
          />
          {!extrasAllowed && (
            <p className="mt-1 text-xs text-muted-foreground">{t('preferences.sellsExtrasLocked')}</p>
          )}
        </CardContent>
      </Card>

      {/* 3. Quy mô đội ngũ */}
      {teamSizeSection && renderSection(teamSizeSection)}

      {/* 4. Hiện phân công giáo viên */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.showTeacherAssignment')}</CardTitle>
          <CardDescription>{t('preferences.showTeacherAssignmentDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Switch
            checked={values.showTeacherAssignment ?? true}
            onCheckedChange={handleToggleShowTeacherAssignment}
            aria-label={t('preferences.showTeacherAssignment')}
          />
        </CardContent>
      </Card>

      {/* 4b. Tổ chức lớp (THI-267 Task 16, S1 frame 3) — beside "Phân công
          giáo viên", same chip pattern as the PreferenceSection rows above.
          Own dirty flag + own mutation (see handleSave): classMode saves
          through PUT /org/settings, not POST /auth/onboarding. */}
      <Card id={CLASS_MODE_CARD_ANCHOR_ID}>
        <CardHeader>
          {/* Fix wave (review M15) — dropped the sr-only `CardDescription`
              that repeated the title verbatim (a screen reader heard the
              label twice); `preferences.classModeHelper` below already
              gives the real description, visually and for assistive tech. */}
          <CardTitle className="text-base">{t('preferences.classMode')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {(
              [
                { value: 'COHORT', labelKey: 'preferences.classModeCohort', icon: Users },
                { value: 'INDIVIDUAL', labelKey: 'preferences.classModeIndividual', icon: User },
              ] as const
            ).map((option) => {
              const isSelected = classMode === option.value;
              const Icon = option.icon;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelectClassMode(option.value)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                    isSelected
                      ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                      : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                  )}
                >
                  <Icon
                    className={cn('w-4 h-4', isSelected ? 'text-brand-600' : 'text-surface-400')}
                    strokeWidth={1.8}
                  />
                  {t(option.labelKey)}
                  {isSelected && <Check className="w-3.5 h-3.5 text-brand-600" strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-surface-500">{t('preferences.classModeHelper')}</p>
        </CardContent>
      </Card>

      {/* 5-6. Kênh liên hệ + Tiền tệ */}
      {tailSections.map((section) => renderSection(section))}

      <div className="flex justify-end">
        <BusyButton
          onClick={handleSave}
          disabled={dirtyFields.size === 0 && !hasClassModeChanges}
          busy={updatePreferences.isPending || updateOrgSettings.isPending}
          busyLabel={t('preferences.saving')}
          className="h-10 px-6 text-sm font-semibold"
        >
          {t('save')}
        </BusyButton>
      </div>
    </div>
  );
}

export function DisplaySection() {
  const { data: orgSettings, isLoading } = useOrgSettings();

  if (isLoading || !orgSettings) return <SectionLoading />;

  const s = orgSettings.settings;
  const initialValues: OnboardingInput = {
    teamSize: (s.teamSize as OnboardingInput['teamSize']) ?? undefined,
    preferredChannel: (s.preferredChannel as OnboardingInput['preferredChannel']) ?? undefined,
    defaultCurrency: (s.defaultCurrency as OnboardingInput['defaultCurrency']) ?? undefined,
    showTeacherAssignment: s.showTeacherAssignment ?? true,
    sellsExtras: s.sellsExtras ?? false,
    businessType: (s.businessType as OnboardingInput['businessType']) ?? 'HOUSEHOLD',
  };

  return <DisplayForm initialValues={initialValues} initialClassMode={orgSettings.classMode} />;
}
